import Image from "next/image";
import Link from "next/link";
import { Calendar, MapPin, Clock, ChevronRight } from "lucide-react";
import { Badge } from "@/shadcn/ui/badge";
import { Comedian } from "@/data/comedians";
import { ShowWithComedian } from "@/data/shows";
import { cn } from "@/utils/cn";
import dayjs from "@punchup/utils/dayjs";

interface EventPageHeroProps {
  show: ShowWithComedian;
  comedian: Comedian;
  heroImage: string | null;
  eventDate: dayjs.Dayjs;
  isUpcoming: boolean;
  isSoldOut: boolean;
  hasWaitlist: boolean;
  isHeatingUp: boolean;
  isLowTickets: boolean;
  isPunchupComedian: boolean;
  isMultiComic: boolean;
  venueSlug?: string;
}

export default function EventPageHero({
  show,
  comedian,
  heroImage,
  eventDate,
  isUpcoming,
  isSoldOut,
  hasWaitlist,
  isHeatingUp,
  isLowTickets,
  isPunchupComedian,
  isMultiComic,
  venueSlug,
}: EventPageHeroProps) {
  return (
    <div
      className={cn(
        "relative overflow-hidden md:h-[500px]",
        hasWaitlist ? "h-[320px]" : "h-[400px]"
      )}
    >
      {/* Background: ambient color wash from the comedian image. Heavy blur +
          scale keeps it a gradient rather than a recognizable zoomed-in crop,
          with the scale pushing the blur's faded edges outside the frame. */}
      <div className="absolute inset-0" aria-hidden>
        {heroImage ? (
          <Image
            src={heroImage}
            alt=""
            fill
            className="object-cover scale-150 blur-3xl"
            priority
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-600 to-purple-700" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
      </div>

      {/* Hero Content with Artist Image */}
      <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-full flex items-center justify-between">
          {/* Left Side - Event Info */}
          <div className="flex-1 py-4 md:py-8">
            <h1 className="text-3xl md:text-6xl font-anton font-bold text-white mb-2 md:mb-4">
              {show.title || `${comedian.display_name} Live`}
            </h1>

            <div
              className={cn(
                "flex flex-wrap gap-3",
                hasWaitlist ? "mb-2 md:mb-6" : "mb-6"
              )}
            >
              {hasWaitlist && (
                <Badge className="bg-yellow-500 text-white text-sm px-3 py-1">
                  Waitlist Open
                </Badge>
              )}
              {isHeatingUp && !hasWaitlist && (
                <Badge className="bg-orange-500 text-white text-sm px-3 py-1">
                  🔥 Heating Up
                </Badge>
              )}
              {isLowTickets && !isSoldOut && (
                <Badge className="bg-red-500 text-white text-sm px-3 py-1">
                  Almost Sold Out
                </Badge>
              )}
              {!isUpcoming && (
                <Badge variant="secondary" className="text-sm px-3 py-1">
                  Past Event
                </Badge>
              )}
            </div>

            {hasWaitlist && isUpcoming && (
              <p
                className={cn(
                  "text-white/90 max-w-2xl",
                  hasWaitlist
                    ? "text-sm md:text-xl mb-3 md:mb-6"
                    : "text-lg md:text-xl mb-6"
                )}
              >
                Join the waitlist to be the first to hear about new dates and
                freed-up tickets.
              </p>
            )}

            <div
              className={cn(
                "flex flex-wrap text-white/90",
                hasWaitlist
                  ? "gap-x-3 gap-y-1 text-sm md:gap-4 md:text-base"
                  : "gap-4"
              )}
            >
              <div className="flex items-center gap-2">
                <Calendar
                  className={cn(
                    hasWaitlist ? "w-4 h-4 md:w-5 md:h-5" : "w-5 h-5"
                  )}
                />
                <span>{eventDate.format("MMM D, YYYY")}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock
                  className={cn(
                    hasWaitlist ? "w-4 h-4 md:w-5 md:h-5" : "w-5 h-5"
                  )}
                />
                <span>{eventDate.format("h:mm A")}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin
                  className={cn(
                    hasWaitlist ? "w-4 h-4 md:w-5 md:h-5" : "w-5 h-5"
                  )}
                />
                {venueSlug ? (
                  <Link href={`/v/${venueSlug}`} className="hover:underline">
                    {show.venue}
                  </Link>
                ) : (
                  <span>{show.venue}</span>
                )}
              </div>
            </div>

            {/* Artist profile link for platform artists. Hidden on mobile for
                the waitlist branch since the form is the priority. */}
            {isPunchupComedian && comedian.slug && !isMultiComic && (
              <Link
                href={`/${comedian.slug}`}
                className={cn(
                  "items-center gap-2 mt-4 text-white/90 hover:text-white hover:underline",
                  hasWaitlist ? "hidden md:inline-flex" : "inline-flex"
                )}
              >
                View {comedian.display_name}&apos;s Profile
                <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>

          {/* Right Side - High Res Artist Image */}
          {heroImage && (
            <div className="hidden md:block relative w-[350px] h-[420px] ml-8">
              <Image
                src={heroImage}
                alt={comedian.display_name}
                fill
                className="object-cover rounded-lg shadow-2xl"
                priority
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
